import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import UserSelectionModal from '../../components/UserSelectionModal';
import ModalConfirm from '../../components/ModalConfirm';
import { useAdmin } from '../../contexts/AdminContext';
import useMonthOptions from '../../hooks/useMonthOptions';

const ModalCourseEnrollment = ({ open, onClose, course, users = [], onSuccess }) => {
  const { addEnrollment } = useAdmin();
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState('monthly');
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, user: null });
  
  // Hook per generare le opzioni dei mesi
  const monthOptions = useMonthOptions(course);

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setSelectedPricing('monthly');
      setSelectedStartDate('');
      setError('');
    }
  }, [open]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };


  const handleSubmit = async () => {
    if (!selectedUser) {
      setError('Seleziona un utente');
      return;
    }

    if (!selectedStartDate) {
      setError('Seleziona il mese di inizio iscrizione');
      return;
    }

    setConfirmModal({ open: true, user: selectedUser });
  };

  const handleConfirmEnrollment = async () => {
    setLoading(true);
    setError('');
    setConfirmModal({ open: false, user: null });
    try {
      const enrollmentData = {
        userId: selectedUser._id,
        courseId: course._id,
        amount: course.pricing?.[selectedPricing] || course.amount,
        paymentMethod: selectedPricing,
        enrollmentStartDate: selectedStartDate
      };

      const result = await addEnrollment(enrollmentData);

      if (result.success) {
        onSuccess && onSuccess(result.data);
        onClose();
      } else {
        setError(result.error || 'Errore durante la creazione dell\'iscrizione');
      }
    } catch (e) {
      setError('Errore durante la creazione dell\'iscrizione');
    } finally {
      setLoading(false);
    
    }
  };

  const handleCloseConfirm = () => {
    setConfirmModal({ open: false, user: null });
  };



  return (
    <>
      <Dialog 
        open={open} 
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          onClose();
        }} 
        maxWidth="md" 
        fullWidth 
        disableEscapeKeyDown
      >
        <DialogTitle>Aggiungi Partecipante al Corso</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Course Information */}
            <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Informazioni Corso
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography><strong>Nome:</strong> {course?.name}</Typography>
                <Typography><strong>Sede:</strong> {course?.venue}</Typography>
                <Typography><strong>Docente:</strong> {course?.teacher ? `${course.teacher.name} ${course.teacher.surname}` : 'Non assegnato'}</Typography>
                <Typography><strong>Data inizio:</strong> {course?.startDate ? dayjs(course.startDate).locale('it').format('DD-MMM-YY') : 'N/A'}</Typography>
                <Typography><strong>Data fine:</strong> {course?.endDate ? dayjs(course.endDate).locale('it').format('DD-MMM-YY') : 'N/A'}</Typography>
                <Typography><strong>Posti disponibili:</strong> {course?.availableSeats || 'N/A'}</Typography>
                <Typography><strong>Tessera associativa:</strong> {course?.isMembershipRequired ? 'Richiesta' : 'Non richiesta'}</Typography>
                {/* <Typography><strong>Importo:</strong> €{course?.amount || 'N/A'}</Typography> */}
              </Box>
            </Box>

            {/* User Selection */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Seleziona Utente
              </Typography>
              {!selectedUser && (
                <Button
                  variant="outlined"
                  onClick={() => setUserModalOpen(true)}
                  sx={{ mb: 1 }}
                >
                  Seleziona Utente
                </Button>
              )}
              {selectedUser && (
                <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
                  <Typography variant='body1' fontWeight='bold' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
                    {selectedUser.name} {selectedUser.surname}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                    {selectedUser.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <Tooltip title='Deseleziona utente'>
                      <IconButton aria-label='Deseleziona utente' color='error' onClick={() => setSelectedUser(null)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Cambia utente'>
                      <IconButton aria-label='Cambia utente' color='primary' onClick={() => setUserModalOpen(true)}>
                        <PersonSearchIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Pricing Options */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Opzioni di Prezzo
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Seleziona Piano di Pagamento</InputLabel>
                <Select
                  value={selectedPricing}
                  onChange={(e) => setSelectedPricing(e.target.value)}
                  label="Seleziona Piano di Pagamento"
                >
                  {course?.pricing?.monthly && (
                    <MenuItem value="monthly">
                      Mensile - €{course.pricing.monthly}
                    </MenuItem>
                  )}
                  {course?.pricing?.annual1 && (
                    <MenuItem value="annual1">
                      Annuale (1 Pagamento) - €{course.pricing.annual1}
                    </MenuItem>
                  )}
                  {course?.pricing?.annual3 && (
                    <MenuItem value="annual3">
                      Annuale (3 Pagamenti) - €{course.pricing.annual3}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Start Date Selection */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Iscrizione valida a partire da
              </Typography>
              <FormControl fullWidth required>
                <InputLabel>Seleziona il mese di inizio iscrizione</InputLabel>
                <Select
                  value={selectedStartDate}
                  onChange={(e) => setSelectedStartDate(e.target.value)}
                  label="Seleziona il mese di inizio iscrizione"
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !selectedUser || !selectedStartDate}
            loading={loading}
          >
            Iscrivi
          </Button>
        </DialogActions>
      </Dialog>

      <UserSelectionModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSelect={handleUserSelect}
        users={users}
      />

      <ModalConfirm
        open={confirmModal.open}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmEnrollment}
        onCancel={handleCloseConfirm}
        title="Conferma Iscrizione"
        message={`Vuoi aggiungere questo membro ${confirmModal.user?.name} ${confirmModal.user?.surname} al corso ${course?.name} con il piano selezionato (€${course?.pricing?.[selectedPricing] || 'N/A'}) a partire da ${selectedStartDate ? dayjs(selectedStartDate).locale('it').format('MMMM YYYY') : 'N/A'}?`}
      />
    </>
  );
};

export default ModalCourseEnrollment; 